import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminUserDto,
  AdminUserList,
  BusinessSettings,
  BusinessSettingsResponse,
  ChangePassword,
  InviteAdmin,
  InviteResult,
  SessionUser,
  UpdateAdmin,
  UpdateBusinessSettings,
  UpdateMe,
} from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   /settings — the team, your own account, and the studio's own details.
   ========================================================================== */

export function useTeam(enabled: boolean) {
  return useQuery({
    queryKey: ['team'],
    queryFn: ({ signal }) => api.get<AdminUserList>('/api/admin/users', signal),
    /* Staff are not allowed to read this and the screen hides the section, so
       asking anyway would spend a 403 on every load to render nothing. */
    enabled,
  });
}

export function useInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteAdmin) => api.post<InviteResult>('/api/admin/users', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useReinvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InviteResult>(`/api/admin/users/${id}/invite`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useUpdateTeamMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: UpdateAdmin }) =>
      api.patch<{ ok: true; user: AdminUserDto }>(`/api/admin/users/${id}`, changes),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useUpdateMe() {
  return useMutation({
    mutationFn: (changes: UpdateMe) =>
      api.patch<{ ok: true; user: SessionUser }>('/api/auth/me', changes),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePassword) =>
      api.post<{ ok: true; message: string }>('/api/auth/password', input),
  });
}

export function useBusinessSettings() {
  return useQuery({
    queryKey: ['business-settings'],
    queryFn: ({ signal }) => api.get<BusinessSettingsResponse>('/api/settings/business', signal),
  });
}

export function useUpdateBusinessSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: UpdateBusinessSettings) =>
      api.patch<BusinessSettingsResponse>('/api/settings/business', changes),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['business-settings'] }),
  });
}

/* --- pure helpers ---------------------------------------------------------- */

/**
 * What state an account is in, in one word.
 *
 * Three states rather than two, because "invited but never logged in" and
 * "active" look identical on a list that only knows enabled/disabled — and the
 * difference is whether somebody is still waiting on a link the owner thinks
 * they sent.
 */
export type AccountState = 'disabled' | 'invited' | 'active';

export function accountState(user: AdminUserDto): AccountState {
  if (user.disabledAt) return 'disabled';
  if (!user.lastLoginAt) return 'invited';
  return 'active';
}

export const ACCOUNT_STATE_LABELS: Record<AccountState, string> = {
  disabled: 'Disabled',
  invited: 'Invited',
  active: 'Active',
};

/**
 * Whether these details are enough to raise a GST invoice.
 *
 * Mirrors `invoiceReady()` on the API. Duplicated deliberately and narrowly:
 * the screen has to say what is missing BEFORE anything is sent, and the API
 * cannot answer a question about a form that has not been submitted. The two
 * are bound by a test.
 */
export function invoiceReady(settings: BusinessSettings): boolean {
  return Boolean(settings.legalName && settings.gstin && settings.addressLine && settings.city);
}

/** Which required invoice fields are still blank, in the owner's words. */
export function missingForInvoice(settings: BusinessSettings): string[] {
  const missing: string[] = [];
  if (!settings.legalName) missing.push('business ka poora naam');
  if (!settings.gstin) missing.push('GSTIN');
  if (!settings.addressLine) missing.push('pata');
  if (!settings.city) missing.push('sheher');
  return missing;
}
